import React, { useCallback } from 'react';
import { useQuery } from 'react-query';
import { Box, Spinner, Alert } from '@razorpay/blade/components';
import { useHistory, useLocation } from 'react-router-dom';
import { routePaths } from 'components/Routes/data';
import api from 'api';
import { aiWorkflowQueryKeys } from 'api/aiWorkflows';
import { showToastViaEvent } from 'utils/ToastEvents';
import { AppError } from 'utils/AppError';
import { AIWorkflowConfig } from '../../types';
import { WorkflowBuilder } from '../WorkflowBuilder';
import { convertApiWorkflowToListItem } from '../../utils';

const EditWorkflowView = () => {
  const history = useHistory();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const workflowId = searchParams.get('workflowId');

  const {
    data: workflowData,
    isLoading,
    error,
  } = useQuery({
    queryKey: [aiWorkflowQueryKeys.workflow, workflowId],
    queryFn: () => api.aiWorkflows.getWorkflowById(Number(workflowId)),
    enabled: !!workflowId,
    staleTime: 0,
    cacheTime: 0,
    refetchOnMount: 'always',
    onError: (err: AppError) => {
      showToastViaEvent({
        text: err.message || 'Failed to load workflow',
        type: 'danger',
        timeout: 3000,
      });
    },
  });

  const handleSave = useCallback(
    (_workflow: AIWorkflowConfig) => {
      showToastViaEvent({
        text: 'Workflow saved successfully',
        type: 'success',
        timeout: 3000,
      });
      history.push(routePaths.aiWorkflow.myFlows);
    },
    [history],
  );

  const handleCancel = useCallback(() => {
    history.push(routePaths.aiWorkflow.myFlows);
  }, [history]);

  if (isLoading) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" height="100%" width="100%">
        <Spinner size="large" accessibilityLabel="Loading workflow..." />
      </Box>
    );
  }

  if (error) {
    return (
      <Box padding="spacing.6">
        <Alert
          color="negative"
          description="Failed to load workflow. Please try again."
          isDismissible
          onDismiss={() => history.push(routePaths.aiWorkflow.myFlows)}
          actions={{
            primary: {
              text: 'Go back',
              onClick: () => history.push(routePaths.aiWorkflow.myFlows),
            },
          }}
        />
      </Box>
    );
  }

  const existingWorkflow = workflowData?.workflow
    ? convertApiWorkflowToListItem(workflowData.workflow)
    : undefined;

  return (
    <WorkflowBuilder existingWorkflow={existingWorkflow} onSave={handleSave} onCancel={handleCancel} />
  );
};

export { EditWorkflowView };
